import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLiveRefresh } from "@/lib/live";
import JobLink from "@/components/JobLink";
import { EmptyRow, ORANGE_BTN, PAGE_SIZE, Pager, Panel, Pill, RollsLink, qty, unit } from "@/components/stores/storesUi";
import { QcDialog } from "@/components/stores/StoreDialogs";

/** Which job(s) returned material into this roll, newest first — opened by clicking the roll's
 * "Returned" figure, the same click-a-square-to-drill-in pattern as the Aging buckets. */
function RollReturnsDialog({ rollNo, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/stores/ledger", { params: { roll_no: rollNo, txn_type: "return", limit: 100 } })
      .then(({ data }) => setRows(data.items))
      .catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [rollNo]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Returns on {rollNo}</DialogTitle>
          <DialogDescription>Which job each returned quantity came back from.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto border border-neutral-200">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead className="bg-neutral-50 label-caps text-[10px] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">From</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-left px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows?.length === 0 && <EmptyRow cols={5} loading={false}>No returns on this roll.</EmptyRow>}
              {(rows || []).map((e) => (
                <tr key={e.id} className="border-t border-neutral-100">
                  <td className="px-3 py-2 font-mono-num text-xs">{e.date}</td>
                  <td className="px-3 py-2 font-mono-num text-xs"><JobLink number={e.bpr_no} tab="materials" /></td>
                  <td className="px-3 py-2 text-xs">{e.department || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(e.qty)} <span className="text-[10px] text-neutral-500">{unit(e.uom)}</span></td>
                  <td className="px-3 py-2 text-xs text-neutral-600 max-w-[14rem] truncate" title={e.reason || ""}>{e.reason || "—"}</td>
                </tr>
              ))}
              {rows === null && <EmptyRow cols={5} loading>…</EmptyRow>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const QC_FILTERS = [
  { value: "all", label: "All QC states" },
  { value: "PENDING", label: "QC pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "NOT APPROVED", label: "Rejected" },
  { value: "OPENING", label: "Opening stock" },
];

export default function RollsTab({ initialQc = "all", initialQ = "" }) {
  const { user } = useAuth();
  const canQc = hasRole(user, "production_manager");
  const [data, setData] = useState(null);
  const [q, setQ] = useState(initialQ);
  const [qc, setQc] = useState(initialQc);
  // A search for a roll or item from elsewhere should also find it once it's used up.
  const [inStock, setInStock] = useState(initialQc === "all" && !initialQ);
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [qcOpen, setQcOpen] = useState(false);
  const [returnsRoll, setReturnsRoll] = useState(null);

  const load = (at = skip, quiet = false) => {
    const params = { skip: at, limit: PAGE_SIZE, available: inStock };
    if (q.trim()) params.q = q.trim();
    if (qc !== "all") params.qc_status = qc;
    return api.get("/stores/rolls", { params })
      .then(({ data }) => {
        setData(data);
        // A background refresh keeps ticks on rolls that are still listed.
        const listed = new Set(data.items.map((r) => r.roll_no));
        setSelected((s) => (quiet ? new Set([...s].filter((roll) => listed.has(roll))) : new Set()));
      })
      .catch((e) => !quiet && toast.error(formatApiError(e)));
  };
  const search = () => { setSkip(0); load(0); };
  useEffect(search, [qc, inStock]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useLiveRefresh("stores", () => load(skip, true));

  const rows = data?.items || [];
  const selectable = (r) => canQc && r.qc_status !== "OPENING";
  const toggle = (roll) => setSelected((s) => {
    const next = new Set(s);
    next.has(roll) ? next.delete(roll) : next.add(roll);
    return next;
  });

  return (
    <div className="space-y-4">
      <div className="border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="rolls-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              className="pl-8 rounded-sm" placeholder="Roll, lot, item code, GRN…" />
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">QC</label>
          <Select value={qc} onValueChange={setQc}>
            <SelectTrigger data-testid="rolls-qc" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{QC_FILTERS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm h-10">
          <input type="checkbox" className="accent-[#FF5A00]" checked={inStock} onChange={(e) => setInStock(e.target.checked)} data-testid="rolls-in-stock" />
          In stock only
        </label>
        <Button variant="outline" className="rounded-sm" onClick={search}>Apply</Button>
        {canQc && selected.size > 0 && (
          <Button data-testid="rolls-qc-open" className={`${ORANGE_BTN} ml-auto`} onClick={() => setQcOpen(true)}>
            <ClipboardCheck size={13} /> Record QC · {selected.size}
          </Button>
        )}
      </div>

      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid="rolls-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                {canQc && <th className="w-8 px-3 py-2.5"></th>}
                <th className="text-left px-3 py-2.5">Roll</th>
                <th className="text-left px-3 py-2.5">Item</th>
                <th className="text-left px-3 py-2.5">Received</th>
                <th className="text-left px-3 py-2.5">QC</th>
                <th className="text-right px-3 py-2.5">Qty in</th>
                <th className="text-right px-3 py-2.5">Issued</th>
                <th className="text-right px-3 py-2.5">Returned</th>
                <th className="text-right px-3 py-2.5">Slit in / out</th>
                <th className="text-right px-3 py-2.5">Balance</th>
                <th className="text-left px-3 py-2.5">Last BPR</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={canQc ? 11 : 10} loading={!data}>No rolls match.</EmptyRow>}
              {rows.map((r) => (
                <tr key={r.roll_no} className={`border-t border-neutral-100 data-row ${selected.has(r.roll_no) ? "bg-orange-50/60" : ""}`}>
                  {canQc && (
                    <td className="px-3 py-2">
                      {selectable(r) && (
                        <input type="checkbox" className="accent-[#FF5A00]" checked={selected.has(r.roll_no)} onChange={() => toggle(r.roll_no)}
                          aria-label={`Select ${r.roll_no}`} />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="font-mono-num text-xs font-semibold">{r.roll_no}</div>
                    <div className="text-[11px] text-neutral-500 font-mono-num flex gap-1">
                      lot {r.master_lot || "—"}
                      {r.parent_roll && <>· from <RollsLink q={r.parent_roll} inline /></>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <RollsLink q={r.item_code} className="font-mono-num text-xs" />
                    <div className="text-[11px] text-neutral-500 truncate max-w-[14rem]">{r.item_name}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-mono-num">{r.received_date || "—"}</div>
                    <div className="text-[11px] text-neutral-500 font-mono-num">{r.grn_no || "Opening"}</div>
                  </td>
                  <td className="px-3 py-2">
                    <Pill value={r.qc_status} />
                    {r.qc_ar_no && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5">AR {r.qc_ar_no}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.received_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.issued_qty ? qty(r.issued_qty) : <span className="text-neutral-300">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono-num">
                    {r.returned_qty ? (
                      <button
                        type="button"
                        data-testid={`roll-returned-${r.roll_no}`}
                        title="See which job(s) returned this"
                        className="rounded-sm px-1 -mx-1 text-[#FF5A00] underline underline-offset-2 hover:bg-orange-50"
                        onClick={(e) => { e.stopPropagation(); setReturnsRoll(r.roll_no); }}
                      >
                        {qty(r.returned_qty)}
                      </button>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num text-xs text-neutral-600">
                    {r.slit_in_qty || r.slit_out_qty ? `${qty(r.slit_in_qty)} / ${qty(r.slit_out_qty)}` : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num font-semibold whitespace-nowrap ${r.balance < 0 ? "text-rose-700" : ""}`}>
                    {qty(r.balance)} <span className="text-[10px] text-neutral-500 font-normal">{unit(r.uom)}</span>
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs"><JobLink number={r.last_bpr_no} tab="materials" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && <Pager skip={data.skip} limit={data.limit} total={data.total} onChange={(s) => { setSkip(s); load(s); }} />}
      </Panel>

      {qcOpen && (
        <QcDialog rollNos={[...selected]} onClose={() => setQcOpen(false)} onDone={() => { setQcOpen(false); setSelected(new Set()); reload(); }} />
      )}
      {returnsRoll && <RollReturnsDialog rollNo={returnsRoll} onClose={() => setReturnsRoll(null)} />}
    </div>
  );
}
